/**
 * navIcons — one icon per sidebar destination.
 *
 * Kept as a path → icon map rather than a field on each nav entry, for two
 * reasons. The nav literals stay readable (a path and a label, not a path, a
 * label and an import), and every icon choice sits in one list where the set
 * can be judged as a set — which is the only way to notice that three screens
 * were about to share a clipboard.
 *
 * CHOOSING THEM. An icon in a labelled nav is not identification, it is
 * recognition: the label already says what the screen is, so the icon's job is
 * to make a row findable on the second visit. That means the useful property is
 * being DISTINCT from its neighbours, not being a perfect metaphor. Where a
 * good metaphor and a distinct shape disagreed, distinctness won — Leave
 * Credits is a wallet rather than a second calendar, because it sits directly
 * under two calendars.
 *
 * Any path without an entry simply renders no icon, so a new screen is never
 * blocked on picking one.
 */
import {
  Archive, BadgeDollarSign, Banknote, BarChart3, Bell, BookOpen, Boxes, Brain,
  Building2, Calculator, CalendarCheck, ClipboardList, CalendarClock, CalendarDays, CalendarRange, Clock,
  Coins, Compass, Contact, FileCheck, FileInput, FileOutput, FilePlus2,
  FileSpreadsheet, FileText, FlaskConical, Gauge, GitBranch, Grid3x3,
  HandCoins, Handshake, History, Inbox, Landmark, Layers, LayoutDashboard, Map,
  MessageSquare, NotebookPen, Package, PackageCheck, PackageMinus, PackagePlus,
  Music2, Percent, PiggyBank, Radar, Receipt, RefreshCcw, Scale, Scan, Scissors,
  Gift, ScrollText, Send, Settings, ShieldCheck, ShoppingBag, SlidersHorizontal, Sparkles,
  Split, Store, Tags, Target, Timer, TrendingUp, Truck, Users, Wallet,
  type LucideIcon,
} from "lucide-react";

export const NAV_ICONS: Record<string, LucideIcon> = {
  // ── Workspace · CRM ──────────────────────────────────────────────────────
  "/crm": Map,
  "/crm/historical": History,
  "/crm/b2b": Handshake,
  "/crm/feedback": MessageSquare,

  // ── E-Commerce Store ─────────────────────────────────────────────────────
  // Three channels in a row, so DISTINCTNESS is the whole job here — more than
  // anywhere else in this map, because the labels are short and similar and
  // the rows sit adjacent. Deliberately NOT brand marks: lucide has none, and
  // reproducing three companies' logos in our nav is a trademark question
  // nobody needs to answer to find a row. Each is the plainest true thing
  // about the channel instead.
  //
  // TikTok is a music note (the platform's own metaphor, and the only note in
  // the map); IG COM is a shopping bag, for the webstore we own; Lazada is a
  // storefront, for the marketplace we rent a stall in. Bag and store are
  // close cousins, which is right — they ARE cousins — and the note between
  // them keeps the pair from touching.
  "/crm/ecommerce": ClipboardList,
  "/crm/tiktok": Music2,
  "/crm/igcom": ShoppingBag,
  "/crm/lazada": Store,

  // ── Modules, as they appear on the launcher ──────────────────────────────
  "/rmc": Boxes,

  // ── HRIS · Payroll ───────────────────────────────────────────────────────
  "/hris": LayoutDashboard,
  "/hris/computation": Calculator,
  "/hris/payroll": Coins,
  "/hris/bankfile": Landmark,
  "/hris/benefits": ShieldCheck,
  "/hris/thirteenth": Gift,
  "/hris/periods": CalendarRange,
  "/hris/payslips": Receipt,
  "/hris/reports": BarChart3,
  "/hris/employees": Users,
  "/hris/201": Contact,
  "/hris/attendance": Clock,
  "/hris/attendance-summary": Timer,
  "/hris/scoreboard": Target,
  "/hris/leaves": CalendarCheck,
  "/hris/leave-credits": Wallet,
  "/hris/leave-recon": Scale,
  "/hris/loans": HandCoins,
  "/hris/leave-types": BookOpen,
  "/hris/holidays": CalendarDays,
  "/hris/schedules": CalendarClock,
  "/hris/workbook": FileSpreadsheet,
  "/hris/1601c": Landmark,
  "/hris/roles": ShieldCheck,
  "/hris/settings": Settings,
  "/hris/discover": Compass,

  // ── Manufacturing ────────────────────────────────────────────────────────
  "/manufacturing/production-grid": Grid3x3,
  "/manufacturing/prodplan": Brain,
  "/manufacturing/work-orders": Radar,
  "/manufacturing/cutting": Scissors,
  "/manufacturing/dispatch": Send,
  "/manufacturing/receiving": PackageCheck,
  "/manufacturing/qc": ShieldCheck,
  "/manufacturing/work-orders/availability": CalendarCheck,
  "/manufacturing/work-orders/sewers": Sparkles,
  "/manufacturing/work-orders/what-if": FlaskConical,
  "/manufacturing/work-orders/subcontractors": Users,
  "/manufacturing/work-orders/metrics": TrendingUp,
  "/manufacturing/work-orders/roster": Gauge,
  "/manufacturing/work-orders/gate1": FileCheck,
  "/manufacturing/work-orders/valve": GitBranch,
  "/manufacturing/production-grid/settings": SlidersHorizontal,
  "/manufacturing/work-orders/settings": Settings,
  "/manufacturing/audit-log": ScrollText,

  // ── Subcontractors · Payroll ─────────────────────────────────────────────
  // Payroll Run is a Calculator rather than a second coin: it sits above two
  // money screens, and what makes it findable is that it is the one that
  // WORKS the figures out rather than listing them.
  "/subcontractors": Users,
  "/subcontractors/payroll": Calculator,
  "/subcontractors/records": Receipt,
  "/subcontractors/bizlink": Landmark,
  "/subcontractors/rates": Tags,
  "/sewer": Users,
  "/sewer/scan": Scan,
  "/sewer/dr": Receipt,
  "/sewer/status": Inbox,
  "/sewer/payout": Receipt,
  "/sewer/me": Users,
  "/manufacturing/sewer-portal": Users,
  "/subcontractors/settings": Settings,

  // ── Inventory · RMC ──────────────────────────────────────────────────────
  "/inventory/rmc": Boxes,
  "/inventory/rmc/dashboard": LayoutDashboard,
  "/inventory/rmc/masterfile": Layers,
  "/inventory/rmc/register-item": PackagePlus,
  "/inventory/rmc/generate": Scan,
  "/inventory/rmc/inbound": Inbox,
  "/inventory/rmc/awaiting-inbound": Truck,
  "/inventory/rmc/outbound": PackageMinus,
  "/inventory/rmc/stock-adjustment": Scale,
  "/inventory/rmc/cycle-count": PackageCheck,
  "/inventory/rmc/reconciliation": RefreshCcw,
  "/inventory/rmc/reorder-planning": Package,
  "/inventory/rmc/suppliers": Building2,
  "/inventory/rmc/alerts": Bell,
  "/inventory/rmc/activity": History,
  "/inventory/rmc/clear-test-data": FlaskConical,

  // ── Accounting ───────────────────────────────────────────────────────────
  "/accounting/sales-invoice": FileText,
  "/accounting/bills-registry": Banknote,
  "/accounting/bookkeeping": NotebookPen,
  "/accounting/daily-expenses": Coins,
  "/outdoor/expenses": Receipt,
  "/outdoor/sales": HandCoins,
  "/accounting/settings": Settings,

  // Accounts payable — the document chain, in the order it is worked.
  "/accounting/ap": Wallet,
  "/accounting/ap/pr": FilePlus2,
  "/accounting/ap/po": ScrollText,
  "/accounting/ap/grn": PackageCheck,
  "/accounting/ap/invoice": FileInput,
  "/accounting/ap/matching": Split,
  "/accounting/ap/pv": BadgeDollarSign,
  "/accounting/ap/or": Receipt,

  // Accounts receivable.
  "/accounting/ar": PiggyBank,
  "/accounting/ar/so": ClipboardList,
  "/accounting/ar/si": FileOutput,
  "/accounting/ar/dr": Truck,
  "/accounting/ar/deposit": Landmark,
  "/accounting/ar/collection": Percent,
  "/accounting/ar/recording": Archive,

};

/** The icon for a destination, or undefined when it has none yet. */
export const navIconFor = (path: string): LucideIcon | undefined => NAV_ICONS[path];
